import React from "react";
import { Link } from "gatsby";

import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import TrustpilotInline from "../components/TrustpilotInline";
import { MicroCombo } from "../components/Trustpilot/TrustpilotWidgets";
import "../../scss/main.scss";

const imagePath = "/assets/marketing-site/images";

const principles = [
	[
		"01",
		"See the whole person",
		"Care begins with a life, not a list of tasks. We listen for the routines, relationships and ambitions that make someone who they are.",
	],
	[
		"02",
		"Earn trust every day",
		"Families invite us into private moments and personal homes. We repay that trust with honesty, accountability and responsive support.",
	],
	[
		"03",
		"Own the details",
		"Safe, dependable care is built from thousands of thoughtful decisions. We notice what is not right and take responsibility for fixing it.",
	],
	[
		"04",
		"Care as one team",
		"Professional carers do extraordinary work, but never alone. Care managers, coordinators and families all contribute to continuity and quality.",
	],
	[
		"05",
		"Use technology thoughtfully",
		"Technology should make care clearer and more human. We use it to support good judgement and stronger relationships, never to replace them.",
	],
];

const team = [
	[
		"jonny.png",
		"Jonny Bottomley",
		"CEO",
		"Personal experience arranging care showed Jonny how much clearer and more human the process could be.",
	],
	[
		"oliver.png",
		"Oliver Ross",
		"COO",
		"Oliver wanted to build the dependable systems and support he would expect for his own family.",
	],
	[
		"sandra-new.jpg",
		"Sandra Doru",
		"Registered Care Manager",
		"Making sure every family can rely on safe, well-led care as needs change.",
	],
	[
		"emma.png",
		"Emma Lennon",
		"Family Care Manager",
		"Helping families feel known and supported throughout the care journey.",
	],
	[
		"muna-new.jpg",
		"Muna Khamdun",
		"Family Care Manager",
		"Bringing reassurance, continuity and thoughtful oversight to each placement.",
	],
	[
		"anne-new.jpg",
		"Anne Githu",
		"Family Care Manager",
		"Helping families feel confident in their care through attentive planning, visits and ongoing support.",
	],
	[
		"daniel-new.png",
		"Daniel Greeves",
		"Family Care Advisor",
		"Making the first conversation about care feel clear, calm and human.",
	],
	[
		"matthew.png",
		"Matthew McCloskey",
		"Family Care Advisor",
		"Helping families understand their options without pressure or unnecessary complexity.",
	],
	[
		"sarah.png",
		"Sarah van der Velden",
		"Family Care Coordinator",
		"Keeping communication and continuity strong throughout each placement.",
	],
	[
		"desre-new.jpg",
		"Desre' Milsom",
		"Family Care Coordinator",
		"Making sure carers and families have responsive practical support when they need it.",
	],
	[
		"orianne.png",
		"Orianne Collins",
		"Quality Assurance Manager",
		"Turning high standards into consistent, dependable everyday practice.",
	],
	[
		"lavinia-new.png",
		"Lavinia Puita",
		"Recruitment Specialist",
		"Helping thoughtful professionals build meaningful and sustainable careers in care.",
	],
	[
		"anna.png",
		"Anna Bondar",
		"Payments Support Specialist",
		"Making the practical side of care simpler and more dependable for carers and families.",
	],
	[
		"tamas.png",
		"Tamas Sagi",
		"Integrations Engineer",
		"Building tools that give care teams better information and more time for people.",
	],
];

const recognition = [
	["our-team-logos/cqc.png", "Care Quality Commission"],
	["our-team-logos/skills-for-care.png", "Skills for Care"],
	["our-team-logos/natwest.png", "NatWest"],
	["our-team-logos/health-investor-awards.png", "HealthInvestor Awards"],
	["our-team-logos/leading-healthcare-awards.png", "Leading Healthcare Awards"],
];

export default function OurTeam() {
	return (
		<div className="container edyn-redesign-shell">
			<Seo
				title="About Edyn Care | Our Story and Team"
				description="Meet the people behind Edyn Care and learn why we are building a more personal, professional and dependable way to arrange live-in care."
				path="/our-team/"
			/>
			<Header />

			<main id="main-content" className="edyn-about">
				<section className="about-hero">
					<div className="site-shell about-hero__heading">
						<p className="eyebrow">About Edyn</p>
						<h1>Who we are, and why we are here.</h1>
					</div>
					<div className="site-shell about-hero__visual">
						<img
							src={`${imagePath}/team/care-awards.jpg`}
							alt="Members of the Edyn team together at the Great British Care Awards"
							width="1920"
							height="1280"
						/>
						<p>
							<span>London, 2025</span>
							Edyn named Best Home Care Employer at the Great British Care
							Awards.
						</p>
					</div>
				</section>

				<section className="about-mission section-space">
					<div className="site-shell about-mission__grid">
						<p className="eyebrow">Why Edyn exists</p>
						<div>
							<h2>
								We believe care should help people remain themselves, in the
								place they know best.
							</h2>
							<div className="about-mission__copy">
								<p>
									Arranging care can arrive suddenly and feel unnecessarily
									complicated. Families are asked to make important decisions at
									a time when clarity, reassurance and human support matter
									most.
								</p>
								<p>
									Edyn brings professional carers, experienced care teams and
									thoughtful technology together to make live-in care safer,
									clearer and more personal from the first conversation onwards.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section
					className="about-trustpilot"
					aria-label="Independent customer reviews"
				>
					<div className="site-shell about-trustpilot__inner">
						<TrustpilotInline />
					</div>
				</section>

				<section className="about-proof" aria-label="Edyn at a glance">
					<div className="site-shell about-proof__grid">
						<p>
							<strong>CQC regulated</strong>
							<span>Accountable for managed care in England</span>
						</p>
						<p>
							<strong>Hundreds of families</strong>
							<span>Supported to arrange care at home</span>
						</p>
						<p>
							<strong>Award-winning team</strong>
							<span>Recognised by the Great British Care Awards</span>
						</p>
					</div>
				</section>

				<section className="about-principles section-space section-dark">
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow eyebrow--light">How we show up</p>
								<h2>The principles behind the care.</h2>
							</div>
							<p>
								These principles guide how we support families, work with
								professional carers and make decisions when the answer is not
								obvious.
							</p>
						</div>
						<div className="about-principles__list">
							{principles.map(([number, title, description]) => (
								<article key={number}>
									<span>{number}</span>
									<h3>{title}</h3>
									<p>{description}</p>
								</article>
							))}
						</div>
					</div>
				</section>

				<section className="about-founders section-space">
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">Our founders</p>
								<h2>A personal reason to build care differently.</h2>
							</div>
							<p>
								Childhood friends Jonny and Oliver founded Edyn after their own
								experiences arranging care for people they loved.
							</p>
						</div>

						<div className="about-founders__visual">
							<img
								src={`${imagePath}/team/jonny_oliver.jpg`}
								alt="Edyn founders Jonny Bottomley and Oliver Ross walking together"
								loading="lazy"
							/>
						</div>

						<div className="about-founders__story">
							<p className="about-founders__lead">
								For something so deeply personal, arranging care felt confusing,
								fragmented and surprisingly impersonal. They believed families
								deserved a service they would feel confident choosing for their
								own loved ones.
							</p>
							<div className="about-founders__bios">
								<article>
									<h3>Jonny Bottomley</h3>
									<span>Co-founder and CEO</span>
									<p>
										Jonny worked in politics before co-founding Edyn. He leads
										the company's mission, growth and commitment to building a
										service families can trust.
									</p>
								</article>
								<article>
									<h3>Oliver Ross</h3>
									<span>Co-founder and COO</span>
									<p>
										Oliver worked in finance before co-founding Edyn. He leads
										the operations and systems that help carers and care teams
										deliver dependable support at home.
									</p>
								</article>
							</div>
						</div>
					</div>
				</section>

				<section className="about-people section-space section-soft">
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">People behind the care</p>
								<h2>Different expertise. One shared responsibility.</h2>
							</div>
							<p>
								Care managers, family advisors, coordinators, recruitment,
								quality, operations and technology all contribute to the service
								a family experiences.
							</p>
						</div>
						<div className="about-people__grid">
							{team.map(([image, name, role, motivation]) => (
								<article key={name}>
									<button
										className="about-people__flip"
										type="button"
										aria-label={`Learn why care matters to ${name}`}
									>
										<span className="about-people__flip-inner">
											<span className="about-people__portrait">
												<img
													src={`${imagePath}/team/${image}`}
													alt=""
													loading="lazy"
												/>
											</span>
											<span className="about-people__motivation">
												<span>Why this work matters</span>
												<strong>{name}</strong>
												<span>{motivation}</span>
											</span>
										</span>
									</button>
									<h3>{name}</h3>
									<p>{role}</p>
								</article>
							))}
						</div>
						<div className="about-people__reviews">
							<p>
								Families can read independent feedback from people who have
								arranged care with Edyn.
							</p>
							<MicroCombo
								classNames="about-people__trustpilot about-people__trustpilot--desktop"
								alignment="right"
								width="270px"
							/>
							<MicroCombo
								classNames="about-people__trustpilot about-people__trustpilot--mobile"
								alignment="left"
								width="270px"
							/>
						</div>
					</div>
				</section>

				<section className="about-recognition section-space">
					<div className="site-shell about-recognition__grid">
						<div>
							<p className="eyebrow">Accountability and recognition</p>
							<h2>Standards that mean something.</h2>
							<p>
								Recognition matters most when it reflects better support for
								families and a stronger place for professional carers to work.
							</p>
						</div>
						<div className="about-recognition__logos">
							{recognition.map(([image, name]) => (
								<div key={name}>
									<img
										src={`${imagePath}/${image}`}
										alt={name}
										loading="lazy"
									/>
								</div>
							))}
						</div>
					</div>
				</section>

				<section className="about-join section-space section-clay">
					<div className="site-shell about-join__grid">
						<div>
							<p className="eyebrow">Work with us</p>
							<h2>Help build a better way to care.</h2>
						</div>
						<div>
							<p>
								We are building a thoughtful, ambitious team across care,
								operations and technology. Explore opportunities to work at Edyn
								or provide care through our platform.
							</p>
							<div className="button-row">
								<a
									className="button button--primary"
									href="https://www.linkedin.com/company/edyn-care/jobs/"
									target="_blank"
									rel="noopener noreferrer"
								>
									View open roles
								</a>
								<Link className="button button--text" to="/become-a-carer/">
									Become a carer <span aria-hidden="true">&#8594;</span>
								</Link>
							</div>
						</div>
					</div>
				</section>
			</main>

			<Footer includeCTA={false} includeBecomeACarer={true} />
		</div>
	);
}
